import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useDebounce } from 'use-debounce';
import { GET_STUDY_CLASSIFICATION } from 'modules/Apollo/queries';
import type {
  GetStudyClassificationQuery,
  GetStudyClassificationQueryVariables,
} from 'generated/graphql';
import { MagicInputBase } from './MagicInputBase';
import { CLASSIFIER_DEBOUNCE_DELAY } from 'config/constants';
import { telemetryContextFor } from 'modules/Apollo/types';

interface MagicStudyInputProps {
  value: string;
  onClassificationChange: (
    classification: GetStudyClassificationQuery['studyClassification']
  ) => void;
  onDescriptionChange: (description: string) => void;
}

export const StudyClassifierInput = ({
  value,
  onClassificationChange,
  onDescriptionChange,
}: MagicStudyInputProps) => {
  const [debouncedValue] = useDebounce(value || '', CLASSIFIER_DEBOUNCE_DELAY);

  const shouldSkipQuery = debouncedValue.trim() === '';

  const { data, error } = useQuery<
    GetStudyClassificationQuery,
    GetStudyClassificationQueryVariables
  >(GET_STUDY_CLASSIFICATION, {
    variables: {
      dicomStudyDescription: debouncedValue,
    },
    skip: shouldSkipQuery,
    fetchPolicy: 'cache-first',
    context: {
      telemetry: { ...APOLLO_QUERY_CONTEXT.telemetry, fetchPolicy: 'cache-first' },
    },
  });

  // NOTE: The endpoint can accept additional parameters. Depending on user feedback, adding the parameters may be desirable.
  useEffect(() => {
    if (shouldSkipQuery) {
      // Clear fields when input is empty
      onClassificationChange(null);
    } else if (data?.studyClassification) {
      onClassificationChange(data.studyClassification);
    }
  }, [data, onClassificationChange, shouldSkipQuery]);

  return (
    <MagicInputBase
      value={value || ''}
      label="Describe study for this protocol"
      subtitle="Type or dictate to auto-generate study criteria"
      placeholder="Enter study description..."
      onValueChange={onDescriptionChange}
      testId="magic-hanging-protocol-input"
      iconLocation="field"
      errorMessage={
        error
          ? error.graphQLErrors?.some((e) => e.message.includes('Could not classify'))
            ? 'Could not classify that; try describing the study differently.'
            : 'Failed to get study classification. Contact support if this persists.'
          : null
      }
    />
  );
};

StudyClassifierInput.displayName = 'StudyClassifierInput';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(StudyClassifierInput);
